import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Segmented } from "@/components/glass/Glass";
import { IconPlay, IconSearch } from "@/components/icons/Icons";
import { haptic } from "@/lib/haptics";
import { cn } from "@/lib/utils";

const PAGE = 40;

/** Season switcher + episode grid. Large series (400+ episodes) are paged & filterable to stay 60fps. */
export function EpisodeList({ movie, currentEpisodeId }) {
  const navigate = useNavigate();
  const seasons = useMemo(() => movie.seasons || [], [movie.seasons]);
  const initialSeason = useMemo(() => {
    const s = seasons.find((x) => x.episodes.some((e) => e.id === currentEpisodeId));
    return (s || seasons[0])?.number ?? 1;
  }, [seasons, currentEpisodeId]);
  const [season, setSeason] = useState(initialSeason);
  const [limit, setLimit] = useState(PAGE);
  const [q, setQ] = useState("");

  const eps = useMemo(() => seasons.find((s) => s.number === season)?.episodes || [], [seasons, season]);
  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return eps;
    return eps.filter((e) => String(e.number) === t || (e.title || "").toLowerCase().includes(t));
  }, [eps, q]);

  if (!seasons.length) return null;
  const total = seasons.reduce((n, s) => n + s.episodes.length, 0);
  const shown = filtered.slice(0, limit);

  return (
    <section className="mt-10" data-testid="episode-list">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-[18px] font-bold tracking-[-0.01em] text-white">Qismlar</h2>
          <p className="mt-0.5 text-[13px] font-medium text-white/45" data-testid="episode-total">{total} ta qism{seasons.length > 1 ? ` · ${seasons.length} mavsum` : ""}</p>
        </div>
        {eps.length > 24 && (
          <label className="flex h-10 w-[180px] items-center gap-2 rounded-full border border-white/10 bg-white/[.05] px-3.5 focus-within:border-white/30">
            <IconSearch size={15} className="text-white/45" />
            <input
              value={q}
              onChange={(e) => {
                setQ(e.target.value.slice(0, 20));
                setLimit(PAGE);
              }}
              inputMode="numeric"
              placeholder="Qism raqami"
              aria-label="Qismni izlash"
              className="h-full min-w-0 flex-1 bg-transparent text-[14px] text-white placeholder:text-white/35 focus:outline-none"
              data-testid="episode-search-input"
            />
          </label>
        )}
      </div>

      {seasons.length > 1 && (
        <div className="pk-scroll-x -mx-4 mt-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
          <Segmented
            options={seasons.map((s) => ({ value: s.number, label: s.title || `${s.number}-mavsum` }))}
            value={season}
            onChange={(v) => {
              setSeason(v);
              setLimit(PAGE);
              setQ("");
            }}
            testId="season-switcher"
          />
        </div>
      )}

      <ul className="mt-4 grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5" data-testid="episode-grid">
        {shown.map((e) => {
          const active = e.id === currentEpisodeId;
          return (
            <li key={e.id}>
              <button
                onClick={() => {
                  haptic(6);
                  navigate(`/watch/${movie.id}?ep=${encodeURIComponent(e.id)}`);
                }}
                className={cn(
                  "group flex h-[64px] w-full items-center gap-3 rounded-[18px] border px-3 text-left transition-[transform,background-color,border-color] duration-200 active:scale-[.97]",
                  active ? "border-white/40 bg-white/[.14]" : "border-white/[.08] bg-white/[.04] hover:border-white/20 hover:bg-white/[.08]"
                )}
                data-testid={`episode-item-${e.number}`}
                aria-current={active ? "true" : undefined}
              >
                <span className={cn("grid h-10 w-10 shrink-0 place-items-center rounded-[12px] text-[14px] font-extrabold", active ? "bg-white text-black" : "bg-white/[.08] text-white")}>
                  {active ? <IconPlay size={15} /> : e.number}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="line-clamp-1 text-[14px] font-semibold text-white">{e.title || `${e.number}-qism`}</span>
                  {(active || (e.title && !e.title.includes(String(e.number)))) && <span className="block text-[12px] font-medium text-white/45">{active ? "Davom ettirish" : `${e.number}-qism`}</span>}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
      {!shown.length && <p className="mt-4 text-[14px] text-white/50" data-testid="episode-empty">Bunday qism topilmadi.</p>}
      {filtered.length > limit && (
        <button onClick={() => setLimit((l) => l + PAGE * 2)} className="mt-4 h-11 w-full rounded-[16px] border border-white/10 bg-white/[.04] text-[14.5px] font-semibold text-white/85 transition-colors duration-150 hover:bg-white/[.08]" data-testid="episode-more-button">
          Yana ko‘rsatish ({filtered.length - limit})
        </button>
      )}
    </section>
  );
}
